import { useEcho } from '@/app/providers/echo-context';
import { useEffect, useRef } from 'react';

export interface CreatedPayload {
    notification?: Record<string, unknown>;
    unread_count?: number;
}

interface Options {
    enabled: boolean;
    onCreated: (payload: CreatedPayload) => void;
    onUnreadCount: (count: number) => void;
}

/**
 * Subscribes to the user's private notification channel.
 *
 * Websocket-only: the previous unread-count polling fallback was removed when
 * notifications were opened to every authenticated user, since it would have
 * multiplied into a request per user per interval. Until the websocket
 * integration lands, the badge refreshes from the server-rendered initial count
 * and from the popover's own fetch. With no Echo instance this hook is a no-op.
 */
export function useNotificationsRealtime({ enabled, onCreated, onUnreadCount }: Options): void {
    const { echo, userId } = useEcho();
    // Stash callbacks in refs so the effect doesn't re-subscribe each render.
    const onCreatedRef = useRef(onCreated);
    const onUnreadCountRef = useRef(onUnreadCount);

    useEffect(() => {
        onCreatedRef.current = onCreated;
        onUnreadCountRef.current = onUnreadCount;
    }, [onCreated, onUnreadCount]);

    useEffect(() => {
        if (!enabled || userId === null || !echo) return;

        const channelName = `users.${userId}`;
        const channel = echo.private(channelName);

        if (!channel) return;

        channel.listen('.notification.created', (payload: CreatedPayload) => {
            onCreatedRef.current(payload);
            if (typeof payload.unread_count === 'number') {
                onUnreadCountRef.current(payload.unread_count);
            }
        });

        channel.listen('.notification.unread-count', (payload: CreatedPayload) => {
            if (typeof payload.unread_count === 'number') {
                onUnreadCountRef.current(payload.unread_count);
            }
        });

        return () => {
            echo.leave(channelName);
        };
    }, [echo, userId, enabled]);
}
